<template>
	<div class="row">

		<div class="col-12">
			<p>
				Please preview the details you've provided and scroll down to submit.
			</p>
			<div class="title-text font-weight-bold">
				General information
			</div>
			<div class="row my-2">
				<div class="col-12 col-md-6 mt-3">
					<div class="form-text">Requested by</div>
					<div class="preview-text">{{ previewRequestedByFullName }}</div>
				</div>
				<div
					class="col-12 col-md-6 mt-3 pl-0"
					v-if="supplierOnboardRequestData.requestedFor"
				>
					<div class="form-text">Requested for</div>
					<div class="preview-text">{{ previewRequestedForFullName }}</div>
				</div>

        <div
            class="col-12 col-md-6 mt-3"
            v-if="watchers && watchers.length > 0"
        >
          <div class="form-text">Watchers</div>
          <div class="preview-text">{{ watcherList }}</div>
        </div>
			</div>
		</div>

		<div class="col-12">
			<div class="container-fluid">
				<div class="row">
					<Detail
						class="col-12"
						position="1"
						name="Description"
						:description="supplierOnboardRequestData.productDescription"
					/>

					<Detail
						class="col-md-6 col-12"
						position="2"
						name="Product name"
						:description="supplierOnboardRequestData.productName"
					/>

					<Detail
						class="col-md-6 col-12"
						position="3"
						name="Requested services start date"
						:description="supplierOnboardRequestData.requestedStartDate"
					/>

					<Detail
						class="col-12"
						position="4"
						name="Procurement category (determines routing within Procurement)"
						:description="
							getPreviewText(supplierServiceCategoryOptions, 'category')
						"
					/>

					<Detail
						class="col-md-6 col-12"
						position="5"
						name="Business justification"
						:description="supplierOnboardRequestData.businessJustification"
					/>

          <Detail
              class="col-md-6 col-12"
              position="6"
              name="Does the supplier have fewer than 5 full time, regular employees?"
              :description="
							supplierOnboardRequestData.supplierEmployeeCountBelowFive | yesnoboolean
						"
          />

          <Detail
              class="col-md-6 col-12"
              position="6.a"
              name="Is your supplier performing one of the following services?"
              :description="supplierOnboardRequestData.service"
          />

          <Detail
              class="col-md-6 col-12"
              position="6.b"
              name="Where is your Supplier located?"
              :description="supplierOnboardRequestData.supplierLocated"
          />

					<Detail
						class="col-md-6 col-12"
						position="7"
						name="What Autodesk company is buying these services / this product?"
						:description="previewMultiSelectOptions"
					/>

				</div>
			</div>
		</div>
		<div class="col-12">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12 title-text font-weight-bold">
            Supplier information
					</div>
					<Detail
						class="col-12"
						position="8"
						name="Have you selected a supplier?"
						:description="
							!supplierOnboardRequestData.needHelpWithSupplier | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="8.a"
						name="Supplier name"
						:description="supplierOnboardRequestData.supplierName"
					/>
					<Detail
						class="col-md-6 col-12"
						position="8.b"
						name="URL for supplier’s website"
						:description="supplierOnboardRequestData.supplierWebsite"
						:capitalize="false"
					/>
					<Detail
						class="col-md-6 col-12"
						position="8.c"
						name="Supplier contact name"
						:description="supplierOnboardRequestData.supplierContactName"
					/>
					<Detail
						class="col-md-6 col-12"
						position="8.d"
						name="Supplier contact role"
						:description="
							getPreviewText(supplierContactRoleOptions, 'supplierContactRole')
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="8.e"
						name="Supplier email"
						:description="supplierOnboardRequestData.supplierContactEmail"
						:capitalize="false"
					/>
					<Detail
						class="col-md-6 col-12"
						position="8.f"
						name="Supplier country"
						:description="supplierOnboardRequestData.supplierCountry"
					/>

          <Detail
              v-if="isCountryUS"
              class="col-md-6 col-12"
              position="8.g"
              name="Have you considered a diverse supplier?"
              :description="
							supplierOnboardRequestData.diverseSupplierConsidered | yesno
						"
          />
				</div>
			</div>
		</div>

    <div class="col-12" v-if="!supplierOnboardRequestData.needHelpWithSupplier && !isICRequest">
      <div class="container-fluid">
        <div class="row">
          <div class="col-12 title-text font-weight-bold">
            Reseller information
          </div>
          <Detail
              class="col-md-6 col-12"
              position="9"
              name="Are you buying this technology through a reseller?"
              :description="
							supplierOnboardRequestData.technologyFromReseller | yesnodontknow
						"
          />
          <Detail
              class="col-md-6 col-12"
              position="9.a"
              name="Reseller name"
              :description="supplierOnboardRequestData.resellerName"
          />
        </div>
      </div>
    </div>

		<div class="col-12">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12 title-text font-weight-bold">
						Data and security
					</div>

          <Detail
						class="col-md-6 col-12"
						position="10"
						name="Approximate cost"
						:description="supplierOnboardRequestData.approximateSpend"
					/>

          <Detail
              class="col-md-6 col-12"
              position="10.a"
              name="Are you planning on paying for this with a corporate card?"
              :description="
							supplierOnboardRequestData.corporateCardPayment"
          />

					<Detail
						class="col-md-6 col-12"
						position="11"
						name="Was this supplier suggested or recommended by a government official, or will this supplier engagement involve any of the following: (a) lobbying or of consulting on government affairs; (b) interacting with government officials on Autodesk’s behalf; or (c) services involving a government official?"
						:description="
							supplierOnboardRequestData.recommendedByGovernment | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="12"
						name="Will this supplier engagement involve them interacting with Autodesk customers on our behalf?"
						:description="
							supplierOnboardRequestData.onBehalfOfAutodeskCustomer | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="13"
						name="Do you or, to your knowledge, does anyone else at Autodesk have a relationship with the supplier or an employee of the supplier that must be disclosed and vetted under our Conflicts of Interest Policy?"
						:description="
							supplierOnboardRequestData.anyRelationshipWithAutodesk | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="14"
						name="Will the supplier’s service or product (i.e., third-party component) be integrated into or released with any Autodesk product, service, or platform? This could include a supplier that is licensing a product or providing a SaaS or PaaS service that will be integrated in an Autodesk product"
						:description="
							supplierOnboardRequestData.integrateWithADSKProduct | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="14.a"
						name="Will the supplier be engaged to provide professional services for the purpose of developing Autodesk product code?"
						:description="
							supplierOnboardRequestData.developAutodeskProductCode | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="15"
						name="Will the supplier’s engagement involve the processing of
								Autodesk personal data?"
						:description="
							supplierOnboardRequestData.personalDataProcessed | yesno
						"
					/>
          <Detail
              class="col-md-6 col-12"
              position="15.a"
              name="What type of personal data will the supplier be processing as a part of this engagement?"
              :description="
							supplierOnboardRequestData.customerDataProcessed | peronalDataOptions
						"
          />
          <Detail
              class="col-md-6 col-12"
              position="16"
              name="Are you acquiring or licensing any third-party technology, service, or software?"
              :description="
							supplierOnboardRequestData.thirdPartyLicenseRequired | yesno
						"
          />
          <Detail
              v-if="isAcquiringThirdPartyTech || isICRequest"
              class="col-md-6 col-12"
              position="16.a"
              name="Will the supplier’s service, application, or product be accessing any Autodesk network or system in any way?"
              :description="
							supplierOnboardRequestData.integrateWithADSKNetwork | yesno
						"
          />
          <Detail
              v-if="isAcquiringThirdPartyTech && !isICRequest"
              class="col-md-6 col-12"
              position="16.b"
              name="Will the supplier be installing any code, cookie, or tag on an Autodesk website?"
              :description="
							supplierOnboardRequestData.cookieInstalledOnADSKNetwork | yesno
						"
          />
          
          <Detail
              v-if="isAcquiringThirdPartyTech"
              class="col-md-6 col-12"
              position="16.c"
              name="Will the supplier have access to or process any customer intellectual property?"
              :description="
							supplierOnboardRequestData.customerIntellectualProperty | yesno
						"
          />
          <Detail
              v-if="isAcquiringThirdPartyTech && !isICRequest"
              class="col-md-6 col-12"
              position="16.d"
              name="If purchasing software, are the licenses installed on an Autodesk device or within the Autodesk network?"
              :description="
							supplierOnboardRequestData.softwareInstalledOnADSKMachine | previewLiscenceOptions
						"
          />
		  <Detail
			v-if="isAcquiringThirdPartyTech && !isICRequest"
			class="col-md-6 col-12"
			position="16.e"
			name="Is this engagement a pilot or proof of concept?"
			:description="
							supplierOnboardRequestData.isPilotOrProofOfConcept | yesno
						"
				/>
		  <Detail
              v-if="isAcquiringThirdPartyTech"
              class="col-md-6 col-12"
              position="16.f"
              name="Will the supplier service or product involve any artificial intelligence or machine learning?"
              :description="
							supplierOnboardRequestData.artificialIntelligenceOrMachineLearning | yesno
						"
          />
		  <Detail
              v-if="isAcquiringThirdPartyTech && isArtificialIntelligenceOrMachineLearning"
              class="col-md-6 col-12"
              position="16.g"
              name="Does this engagement involve Generative AI?"
              :description="
							supplierOnboardRequestData.generativeAI
						"
          />

	  <Detail
              v-if="isAcquiringThirdPartyTech && isArtificialIntelligenceOrMachineLearning"
              class="col-md-6 col-12"
              name="Generative AI Attestation submitted?"
              :description="
							supplierOnboardRequestData.genAIAttestationSubmitted | boolToYesNo
						"
          />



				</div>
			</div>
		</div>

		<div class="col-12" v-if="isCandidateForShortForm()">
			<div class="container-fluid">
				<div class="row">
					<div class="col-12 title-text font-weight-bold">
						Follow-up questions
					</div>
					<Detail
						class="col-md-6 col-12"
						position="17"
						name="Purchase type"
						:description="
							getPreviewText(supplierPurchaseTypeOptions, 'purchaseType')
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="18"
						name="Is there a current master agreement in place with this supplier?"
						:description="supplierOnboardRequestData.masterAgreementExists"
					/>
					<Detail
						class="col-md-6 col-12"
						position="19"
						name="Will the services be completed within one year?"
						:description="
							supplierOnboardRequestData.deliveredWithInYear | yesno
						"
					/>
					<Detail
						class="col-md-6 col-12"
						position="20"
						name="Will the supplier provide tax, audit, or legal services?"
						:description="getPreviewText(supplierServiceType, 'serviceType')"
					/>
				</div>
			</div>
		</div>

    <div class="col-12">
      <preview-attachments :filteredFormData="supplierOnboardRequestData" :viewAttachment="false"/>
    </div>

		<div class="col-12 px-xl-0 mt-4">
			<div
				class="d-flex flex-column align-items-center justify-content-xl-end flex-xl-row"
			>
				<button
					class="flat-btn gray-btn close-btn btn mx-2 my-3"
					@click="navigateToHome"
					:disabled="isLoading"
				>
					Cancel
				</button>
				<button
					class="flat-btn gray-btn close-btn btn mx-2 my-3"
					@click="saveSupplierOnboardRequest(true)"
					:disabled="isLoading"
				>
					Save Draft
				</button>
				<button
					class="btn primary-blue-btn flat-btn mt-2 mt-xl-0 ml-xl-2"
					@click="onSubmitOneIntakeForm"
					:disabled="isLoading"
				>
					Submit
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import supplierRequestMixin from "@/mixins/supplierRequestMixin";
import feedbackMixin from "../../mixins/feedbackMixin";
import { mapMutations, mapGetters } from "vuex";
import Detail from "./PreviewDetails/Detail";
import CONSTANTS from "@/store/constants";
import PreviewAttachments from "@/components/Payments/PreviewAttachments";

export default {
	name: "PreviewDetails",
	mixins: [supplierRequestMixin,feedbackMixin],
	filters: {
		yesno: function(value) {
      //Specifically checking if the options selected were Yes/No , if not answered then "N/A", to be used.
      if (value){
        return "Yes"
      }else if (value === false){
        return "No"
      }else return "N/A"
		},
    yesnodontknow: function (value) {
      if (value) {
        return CONSTANTS.ONE_INTAKE_RADIO_OPTIONS.find(option => option.text === value).displayText
      }
    },
	yesnoboolean:function (value) {
      if (value != undefined) {
        return CONSTANTS.ONE_INTAKE_RADIO_BOOLEAN_EMPLOYEE.find(option => option.value === value).text
      }
    },
    previewLiscenceOptions: function (value){
      if (value){
        return CONSTANTS.ONE_INTAKE_LISCENCE_INSTALLED_RADIO_OPTIONS.find(option => option.text === value).displayText
      }
    },
    peronalDataOptions: function (value){
      if (value) {
        return CONSTANTS.ONE_INTAKE_PERSONAL_DATA_OPTIONS.find(option => option.text === value).displayText
      }
    },
    boolToYesNo: function (value) {
      return value !== undefined ? (CONSTANTS.ONE_INTAKE_RADIO_BOOLEAN.find(option => option.value === value)).text : "No";
    }
	},
  data() {
	  return {
	    previewMultiSelectOptions: ''
    }
  },
	methods: {
		...mapMutations(["setCurrentTab"]),
    updatePreviewMultiSelectOptions() {
        if (this.supplierOnboardRequestData.companyBuyingServices && this.supplierOnboardRequestData.companyBuyingServices.length > 0) {
          this.previewMultiSelectOptions =  this.supplierOnboardRequestData.companyBuyingServices.map(option => option.text).join(", ");
        }
        if (this.supplierOnboardRequestData.companyBuyingServiceCheckBox && this.supplierOnboardRequestData.companyBuyingServiceCheckBox.length > 0)
          this.previewMultiSelectOptions.length > 0 ?  this.previewMultiSelectOptions =  this.previewMultiSelectOptions + ", " + this.supplierOnboardRequestData.companyBuyingServiceCheckBox.map(option => option.text).join(", ") : this.previewMultiSelectOptions = this.supplierOnboardRequestData.companyBuyingServiceCheckBox.map(option => option.text).join(", ")
        else
          return null;
    },
		/* The supplierOnboardRequestData object has 'values' of the options that we displayed to the user, not 'text'.
      We need to extract text by finding and displaying to user from corresponding options. .*/
		getPreviewText(arrayObj, filteredValue) {
			let result = "";

			if (arrayObj) {
				let data = arrayObj.find(
					(item) =>
						item.value === this.supplierOnboardRequestData[filteredValue]
				);
				result = data && data.text;
			}

			return result;
		},

	// Adding this method for trigeering events on submit 

	onSubmitOneIntakeForm(){
		this.saveSupplierOnboardRequest(false);
		this.setFeedback(CONSTANTS.FEEDBACK_TIMEOUT_IN_MILLIS)
	}
	},
	computed: {
		...mapGetters([
			"supplierOnboardRequestData",
			"isLoading",
			"supplierContactRoleOptions",
			"supplierPurchaseTypeOptions",
			"supplierServiceType",
			"supplierServiceCategoryOptions",
			"approximateSpendOptions",
      "watchers"
		]),

		/*Need a local copy of full name because upon submit, we delete fullname before hitting the api, so full name would appear blank if used from store.
		 * Below code deep copies the object and stores in a computed prop.*/

		previewRequestedByFullName() {
			return this.supplierOnboardRequestData.requestedByFullName ? this.supplierOnboardRequestData.requestedByFullName.slice(0) : null
		},
		previewRequestedForFullName() {
			return this.supplierOnboardRequestData.requestedForFullName ? this.supplierOnboardRequestData.requestedForFullName.slice(0): null
		},
    watcherList() {
      if (this.watchers && this.watchers.length > 0)
        return this.watchers.map(user => user.fullName).join(", ");
      else
        return null;

    }
	},
	created() {
		window.scrollTo(0, 0);
		this.updatePreviewMultiSelectOptions()
	},
	components: { Detail ,PreviewAttachments},
};
</script>

<style scoped>
.preview-text {
	margin-top: 6px;
}

.preview-sentence-case:first-letter {
	text-transform: capitalize;
}

.edit-link img {
	width: 25px;
}

.form-text {
	font-weight: bold;
	margin-top: 10px;
}

.title-text {
	padding: 0px;
	margin: 8px 0px;
}

.flat-btn {
	margin: 0;
}
</style>
